import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les articles
      const postsUrl = selectedCategory === 'all' 
        ? `${API}/blog/posts`
        : `${API}/blog/posts?category=${selectedCategory}`;
      
      const postsRes = await axios.get(postsUrl);
      setPosts(postsRes.data);

      // Charger les catégories
      const categoriesRes = await axios.get(`${API}/blog/categories`);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Erreur chargement blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.1) 0%, rgba(201, 169, 97, 0.05) 100%)',
        padding: '80px 20px 60px',
        textAlign: 'center',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
            Blog Beauté & Bien-être
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            Conseils d'experts, tendances beauté et astuces pour prendre soin de vous au quotidien
          </p>
        </div>
      </section>

      {/* Filtres de catégories */}
      <section style={{ padding: '40px 20px' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            <button
              onClick={() => setSelectedCategory('all')}
              className="body-regular"
              style={{
                padding: '10px 24px',
                background: selectedCategory === 'all' ? 'var(--gold-primary)' : 'white',
                color: selectedCategory === 'all' ? 'white' : 'var(--text-primary)',
                border: selectedCategory === 'all' ? 'none' : '1px solid var(--border-medium)',
                borderRadius: '24px',
                cursor: 'pointer',
                fontWeight: selectedCategory === 'all' ? 600 : 400,
                transition: 'all 0.2s ease'
              }}
            >
              Tous les articles
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className="body-regular"
                style={{
                  padding: '10px 24px',
                  background: selectedCategory === cat ? 'var(--gold-primary)' : 'white',
                  color: selectedCategory === cat ? 'white' : 'var(--text-primary)',
                  border: selectedCategory === cat ? 'none' : '1px solid var(--border-medium)',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  fontWeight: selectedCategory === cat ? 600 : 400,
                  transition: 'all 0.2s ease'
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Liste des articles */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Chargement des articles...
              </p>
            </div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Aucun article disponible pour le moment.
              </p>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '12px' }}>
                Revenez bientôt pour découvrir nos conseils beauté ! ✨
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '32px'
            }}>
              {posts.map((post) => (
                <article
                  key={post.id}
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                >
                  {post.featured_image && (
                    <div style={{ height: '220px', overflow: 'hidden' }}>
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                  
                  <div style={{ padding: '24px' }}>
                    {/* Catégorie */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <Tag size={14} style={{ color: 'var(--gold-primary)' }} />
                      <span className="body-small" style={{ color: 'var(--gold-primary)', fontWeight: 600 }}>
                        {post.category}
                      </span>
                    </div>

                    {/* Titre */}
                    <h2 className="heading-2" style={{ marginBottom: '12px', lineHeight: '1.3' }}>
                      {post.title}
                    </h2>

                    {/* Extrait */}
                    <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.6' }}>
                      {post.excerpt}
                    </p>

                    {/* Métadonnées */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginBottom: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid var(--border-light)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <User size={14} style={{ color: 'var(--text-secondary)' }} />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {post.author}
                        </span>
                      </div>
                    </div>

                    {/* Lien "Lire la suite" */}
                    <Link
                      to={`/blog/${post.slug}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--gold-primary)',
                        textDecoration: 'none',
                        fontWeight: 600,
                        transition: 'gap 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.gap = '12px'}
                      onMouseLeave={(e) => e.currentTarget.style.gap = '8px'}
                    >
                      Lire la suite
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
